import { hexToLab, type LAB } from "./color-engine";

/**
 * Reads the RGB value at a single pixel from ImageData and converts it to
 * Lab. Pure function (no canvas/DOM dependency beyond the ImageData
 * shape itself) so it's unit-testable without a real browser.
 */
export function pixelToLab(imageData: {
  data: Uint8ClampedArray;
  width: number;
}, x: number, y: number): LAB {
  const px = Math.floor(x);
  const py = Math.floor(y);
  const index = (py * imageData.width + px) * 4;
  const r = imageData.data[index];
  const g = imageData.data[index + 1];
  const b = imageData.data[index + 2];
  const hex = `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  return hexToLab(hex);
}

/**
 * Computes target width/height to downscale an image to, capping the
 * longest edge at `maxDimension` while preserving aspect ratio.
 *
 * Applied before drawing an uploaded photo to canvas — a phone photo can
 * be 4000px+ on its long edge, and reading/processing pixels at that
 * resolution costs real time on a mid-range phone's CPU for no matching
 * benefit (a single tapped pixel's color doesn't get more accurate at
 * higher resolution). Capping this keeps the scanner's interaction speed
 * consistent across device classes rather than mobile lagging behind
 * desktop on large photos.
 */
export function computeDownscaledSize(
  naturalWidth: number,
  naturalHeight: number,
  maxDimension = 1200
): { width: number; height: number } {
  const longestEdge = Math.max(naturalWidth, naturalHeight);
  if (longestEdge <= maxDimension) {
    return { width: naturalWidth, height: naturalHeight };
  }
  const scale = maxDimension / longestEdge;
  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale),
  };
}
